import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type CalendarEvent,
  type CalendarLabels,
  type CalendarProps,
  Calendar as BaseCalendar,
} from '@valwin/facto/components';

import { type SupportedLanguage } from '~shared/i18n';

type AppCalendarProps<TEvent extends CalendarEvent> = Omit<
  CalendarProps<TEvent>,
  'labels' | 'locale'
>;

/** Facto calendar with the app's translations */
export function Calendar<TEvent extends CalendarEvent>(
  props: AppCalendarProps<TEvent>
) {
  const { t, i18n } = useTranslation();
  const locale: SupportedLanguage = i18n.language === 'es' ? 'es' : 'fr';

  const labels = useMemo<CalendarLabels>(
    () => ({
      today: t('common today'),
      close: t('common close'),
      previous: t('common previous'),
      next: t('common next'),
      showMore: (count) => t('calendar showMore', { count }),
      views: {
        day: t('calendar view day'),
        week: t('calendar view week'),
        month: t('calendar view month'),
      },
      displays: {
        calendar: t('calendar display calendar'),
        list: t('calendar display array'),
      },
    }),
    [t]
  );

  return <BaseCalendar {...props} locale={locale} labels={labels} />;
}
